import React, { FC, useEffect, useState } from 'react'
import AppPageComponent from '@components/page/appPage.component'
import PageHeaderComponent from '@components/page/pageHeader.component'
import PageBodyComponent from '@components/page/pageBody.component'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@store/store'
import {
  fetchAllPlaylists,
  PlaylistModel,
  selectPlaylistById,
  selectPlaylistLoading,
} from '@store/slices/playlist.slice'
import { DjangoFilter, SortOrder } from '@store/slices/generic.slice'
import { Col, ListGroup, Row } from 'react-bootstrap'
import InfiniteScroll from 'react-infinite-scroll-component'
import { EntityId, unwrapResult } from '@reduxjs/toolkit'
import {
  selectAllAuthors,
  selectAuthorByEmail,
  selectAuthorsLoading,
} from '@store/selectors/author.selectors'
import { fetchAllAuthors } from '@store/thunk/author.thunk'
import { AppSelectComponent } from '@components/shared/appSelect.component'
import { AppDatePicker } from '@components/shared/appDatePicker.component'
import { toast } from 'react-toastify'
import {
  formatDate,
  getLocalizedDateTimeString,
  toDateHourString,
  toHourMinString,
} from '@shared/tools/time.tools'
import { Link } from 'react-router-dom'
import { enumToOptions } from '@shared/tools/enum.tools'
import { RadioStationsEnum } from '@shared/enums'
import { AuthorModel } from '@store/slices/author.slice'
import { PlaylistListRow } from '@components/playlist/playlistList.component'

const ArchivePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const [playlistIds, setPlaylistIds] = useState<EntityId[]>([])
  const isPlaylistLoading = useSelector(selectPlaylistLoading)
  const [playlistsRefreshed, setPlaylistsRefreshed] = useState<boolean>(false)
  const [pingMoreData, setPingMoreData] = useState(false)
  const authors = useSelector(selectAllAuthors)
  const authorsLoadin = useSelector(selectAuthorsLoading)

  useEffect(() => {
    if ((!authors || authors.length === 0) && !authorsLoadin) {
      dispatch(fetchAllAuthors())
    }
  }, [authors])

  const fetchMoreDataPing = () => {
    setPingMoreData(true)
  }

  return (
    <AppPageComponent>
      <PageHeaderComponent>
        <h3 className="my-2">Archiv</h3>
        <div className="my-2">
          <PlaylistFilterForm
            filteredPlaylistIds={playlistIds}
            setPlaylistIds={setPlaylistIds}
            setPlaylistRefreshed={setPlaylistsRefreshed}
            fetchMoreDataPing={pingMoreData}
            fetchMoreDataPinged={() => setPingMoreData(false)}
          />
        </div>
      </PageHeaderComponent>
      <PageBodyComponent>
        <div className="my-2">
          <ListGroup>
            <ListGroup.Item active style={{ zIndex: 0 }}>
              <Row className="d-flex justify-content-between ">
                <Col style={{ maxWidth: '50px' }}>ID</Col>
                <Col xs={2}>Čas</Col>
                <Col style={{ maxWidth: '105px' }}>Počet slotů</Col>
                <Col xs={4}>Vytvořeno</Col>
                <Col xs={4}>Upraveno</Col>
              </Row>
            </ListGroup.Item>
            {playlistsRefreshed && (
              <InfiniteScroll
                dataLength={playlistIds.length}
                hasMore={!isPlaylistLoading}
                next={fetchMoreDataPing}
                loader={
                  <h4 className="mx-auto mt-5 w-25 text-center text-secondary">
                    Pro načtení další playlistů změntě parametry vyhledávání
                  </h4>
                }
                height={700}
              >
                {playlistIds.map((playlistId) => (
                  <PlaylistListRow
                    key={playlistId}
                    playlistId={playlistId}
                  ></PlaylistListRow>
                ))}
              </InfiniteScroll>
            )}
          </ListGroup>
        </div>
      </PageBodyComponent>
    </AppPageComponent>
  )
}

export default ArchivePage

const PlaylistFilterForm: FC<{
  filteredPlaylistIds: EntityId[]
  setPlaylistIds: (playlistIds: EntityId[]) => void
  setPlaylistRefreshed: (refreshed: boolean) => void
  fetchMoreDataPing: boolean
  fetchMoreDataPinged: () => void
}> = ({
  filteredPlaylistIds,
  setPlaylistIds,
  setPlaylistRefreshed,
  fetchMoreDataPing,
  fetchMoreDataPinged,
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const authors = useSelector(selectAllAuthors)
  const authorsLoading = useSelector(selectAuthorsLoading)
  const playlistLoading = useSelector(selectPlaylistLoading)
  const [page, setPage] = useState(0)
  const [sorting, setSorting] = useState<number | null>(1)
  const [dateFilter, setDateFilter] = useState<Date | null>(new Date())
  const [hourFromFilter, setHourFromFilter] = useState<string | null>('0')
  const [hourToFilter, setHourToFilter] = useState<string | null>('6')
  const stations = ['EXPRESS']
  const [stationFilter, setStationFilter] = useState<string | null>(null)
  const [authorFilter, setAuthorFilter] = useState<EntityId | null>(null)
  const stationOptions = enumToOptions(RadioStationsEnum)
  const sortingOptions: { id: EntityId; label: string }[] = [
    {
      id: 1,
      label: 'Upraveno sestupně',
    },
    {
      id: 2,
      label: 'Upraveno vzestupně',
    },
  ]
  const hourOptions: { id: EntityId; label: string }[] = Array.from(
    { length: 24 },
    (_, i) => i
  ).map((hour) => {
    const value = hour < 10 ? `0${hour}:00` : `${hour}:00`
    return { id: hour.toString(), label: value }
  })

  const authorOptions: { id: EntityId; label: string }[] = authors
    .map((author) => ({ id: author.username, label: author.username }))
    .sort((a, b) => a.label.localeCompare(b.label))

  useEffect(() => {
    if (!playlistLoading && filteredPlaylistIds.length == 0) {
      fetchFilteredPlaylists()
    }
  }, [])

  useEffect(() => {
    if (fetchMoreDataPing) {
      fetchMoreData()
      fetchMoreDataPinged()
    }
  }, [fetchMoreDataPing])

  useEffect(() => {
    if (!playlistLoading && stations.length > 0 && authors.length > 0) {
      fetchFilteredPlaylists()
    }
  }, [
    dispatch,
    page,
    stationFilter,
    sorting,
    dateFilter,
    hourFromFilter,
    hourToFilter,
    authorFilter,
  ])

  useEffect(() => {
    if (authors.length == 0 && !authorsLoading) {
      dispatch(fetchAllAuthors())
    }
  }, [])

  const fetchFilteredPlaylists = () => {
    const filter = new DjangoFilter<PlaylistModel>()

    // Set limit and offset for Pagination
    const itemsPerPage = 18
    filter.setLimit(itemsPerPage)
    filter.setOffset(itemsPerPage * page)

    // Set Sort Order
    let sortValue: SortOrder | null
    switch (sorting) {
      case 1:
        sortValue = SortOrder.DESCENDING
        break
      case 2:
        sortValue = SortOrder.ASCENDING
        break
      default:
        sortValue = null
    }
    if (sortValue) {
      filter.setSortOrder(sortValue)
    }

    // Add Sort Field(s)
    filter.addSortField('updated_at')

    const fromTimeString = getFromTime()
    const toTimeString = getToTime()

    // Add any other required filters
    filter.addFilter({
      stationId: stationFilter ? stationFilter : undefined,
      fromTime: fromTimeString ? fromTimeString : undefined,
      toTime: toTimeString ? toTimeString : undefined,
      created_by__username: authorFilter ? authorFilter : undefined,
    })

    // Dispatch the fetchAllPlaylists method with the filter
    dispatch(fetchAllPlaylists(filter))
      .then(unwrapResult)
      .then((playlists: PlaylistModel[]) => {
        setPlaylistIds([
          ...filteredPlaylistIds,
          ...playlists.map((playlist) => playlist.id),
        ])
        setPlaylistRefreshed(true)
      })
  }

  const getFromTime = (): string | null => {
    if (dateFilter) {
      const date = new Date(dateFilter)
      if (hourFromFilter) {
        return getLocalizedDateTimeString(date, hourFromFilter)
      } else {
        return getLocalizedDateTimeString(date, '0')
      }
    } else {
      return null
    }
  }

  const getToTime = (): string | null => {
    if (dateFilter) {
      const date = new Date(dateFilter)
      if (hourToFilter) {
        return getLocalizedDateTimeString(date, hourToFilter)
      } else {
        return getLocalizedDateTimeString(date, '23')
      }
    } else {
      return null
    }
  }

  const handleFetchParamsChange = () => {
    setPlaylistIds([])
    setPage(0)
  }

  const handleStationFilterChange = (value: EntityId | null) => {
    handleFetchParamsChange()
    setStationFilter(value as string)
  }
  const fetchMoreData = () => {
    setPage((page) => page + 1)
  }

  const handleSortFilterChange = (value: EntityId | null) => {
    handleFetchParamsChange()
    setSorting(value as number)
  }

  const handleDateChange = (value: Date | null) => {
    handleFetchParamsChange()
    if (!value) {
      setHourToFilter(null)
      setHourFromFilter(null)
    }
    setDateFilter(value)
  }

  const handleHourFromChange = (hour: EntityId | null) => {
    const hourToNum: number = parseInt(hourToFilter as string)
    const hourNum: number = parseInt(hour as string)
    if (
      hour &&
      ((hourToFilter &&
        Number.parseInt(hourToFilter as string) >
          Number.parseInt(hour as string)) ||
        !hourToFilter)
    ) {
      handleFetchParamsChange()
      setHourFromFilter(hour as string)
    } else if (hour && hourToFilter && hourToNum < 23) {
      handleFetchParamsChange()
      setHourToFilter((hourNum + 1).toString())
      setHourFromFilter(hour as string)
    } else if (!hour) {
      handleFetchParamsChange()
      setHourFromFilter(null)
    } else {
      toast.warning('Playlist musí být v rámci jednoho dne')
    }
  }

  const handleHourToChange = (value: EntityId | null) => {
    if (
      !hourFromFilter ||
      !value ||
      (hourFromFilter &&
        value &&
        Number.parseInt(hourFromFilter) < Number.parseInt(value as string))
    ) {
      handleFetchParamsChange()
      setHourToFilter(value as string)
    } else {
      toast.warning('Hodina DO musí být vyšší než hodina OD')
    }
  }

  const handleAuthorChange = (value: EntityId | null) => {
    handleFetchParamsChange()
    setAuthorFilter(value)
  }
  return (
    <div className={'d-flex gap-2'}>
      <AppSelectComponent
        options={stationOptions}
        onSelectChange={handleStationFilterChange}
        valueId={stationFilter ? stationFilter : undefined}
        placeholder={'Vyberte stanici'}
        isClearable={true}
        customStyles={{
          input: {
            minWidth: '100px',
          },
        }}
      ></AppSelectComponent>
      <AppDatePicker
        startDate={dateFilter ? dateFilter : undefined}
        handleDateChange={handleDateChange}
        isClearable={true}
      />
      <AppSelectComponent
        onSelectChange={handleHourFromChange}
        options={hourOptions}
        valueId={hourFromFilter ? hourFromFilter : undefined}
        placeholder={'Od hodiny'}
        isClearable={true}
        isDisabled={!dateFilter}
      ></AppSelectComponent>
      <AppSelectComponent
        onSelectChange={handleHourToChange}
        options={hourOptions}
        valueId={hourToFilter ? hourToFilter : undefined}
        placeholder={'Do Hodiny'}
        isClearable={true}
        isDisabled={!dateFilter}
      ></AppSelectComponent>
      <AppSelectComponent
        options={authorOptions}
        onSelectChange={handleAuthorChange}
        placeholder={'Vytvořil autor'}
        valueId={authorFilter ? authorFilter : undefined}
        isClearable={true}
        customStyles={{
          input: {
            minWidth: '100px',
          },
        }}
      ></AppSelectComponent>
      <AppSelectComponent
        options={sortingOptions}
        onSelectChange={handleSortFilterChange}
        valueId={sorting ? sorting : undefined}
        placeholder={'Seřadit'}
        customStyles={{
          input: {
            minWidth: '100px',
          },
        }}
      ></AppSelectComponent>
    </div>
  )
}
